/**
 * AI 指挥办公室 — 部门/场景配置
 * 场景 = 空旷高速公路指挥大厅背景(hall-bg) + 每部门一张独立平台立绘(真透明PNG)
 * 布局 = 左右两个分区：左=业主单位区(金色渲染/圆形底座)，右=外部协作区(紫色渲染/六边形底座)
 * 新增部门：在 DEPTS 加一项 + 在对应分区 slots 加一个坐标位即可。
 */

export const HALL_BG = '/aco-assets/hall-bg.png';

export type DeptCategory = 'internal' | 'external';

/** 场景版式：业主/外部是两个可切换的全屏"街景"。
 * 部门平台沿弧形排在上方（中间高远、两侧低近），弧形围出的中央区域=指挥信息台。
 * slots 为平台锚点(占场景宽/高百分比，锚点=平台底部中心)。 */
export const SCENES: Record<DeptCategory, {
  label: string; sub: string; tint: string; tintSoft: string; glyph: string;
  bg: string;
  /** 槽位=工作台底边中心，必须落在大厅地面上；scale=近大远小 */
  slots: Array<{ x: number; y: number; scale?: number }>;
}> = {
  internal: {
    label: '业主单位', sub: '高速公路运营管理', tint: '#fbbf24', tintSoft: 'rgba(251,191,36,.16)', glyph: '●',
    bg: '/aco-assets/hall-grid.webp',
    slots: [
      { x: 10, y: 82, scale: 1.12 }, { x: 35, y: 55, scale: 0.76 }, { x: 65, y: 55, scale: 0.76 }, { x: 90, y: 82, scale: 1.12 },
    ],
  },
  external: {
    label: '外部协作单位', sub: '技术与设计伙伴', tint: '#c4b5fd', tintSoft: 'rgba(196,181,253,.15)', glyph: '⬡',
    bg: '/aco-assets/hall-grid-ext.webp',
    slots: [
      { x: 30, y: 72, scale: 0.98 }, { x: 70, y: 72, scale: 0.98 },
    ],
  },
};

/** 智能体形象图（黑底原图已转真透明） */
export const AGENT_ICONS: Record<'general' | 'cabin' | 'devteam', string> = {
  general: '/aco-assets/agent-general.png',
  cabin: '/aco-assets/agent-biz.png',
  devteam: '/aco-assets/agent-dev.png',
};

export interface PlannedAgent {
  id: string;
  name: string;
  /** 特殊内建智能体：devteam=数字人团队(转研发) */
  special?: 'devteam';
}

export interface DeptConf {
  id: string;
  name: string;
  /** 曾用名，仅用于识别历史任务，不在界面展示。 */
  aliases?: string[];
  sub: string;
  desc: string;
  accent: string;       // 主题色
  accentSoft: string;   // 主题色弱化(光晕)
  img: string;          // 平台立绘(透明底)
  category: DeptCategory;
  /** 部门职责清单（点部门后中央舞台展示） */
  duties?: string[];
  /** 业务智舱真实智能体归属匹配(展示分组用) */
  match: RegExp;
  /** 规划中的业务智能体(灰=待接入; special 除外) */
  planned: PlannedAgent[];
}

export const DEPTS: DeptConf[] = [
  {
    // ⚠️ id 沿用 'toll'（PERSONAS / HALL_PAGES / 平台图 / 智能体映射都按 id 关联，改名不改 id 最稳）
    id: 'toll', name: '工程技术部', sub: '改扩建 · 养护 · 工程管理',
    desc: '负责改扩建、养护技术、工程项目管理与招投标管理，可交办工程技术类文档、方案与专项分析。',
    duties: ['改扩建', '养护技术', '工程项目管理', '招投标管理'],
    accent: '#f59e0b', accentSoft: 'rgba(245,158,11,.35)',
    img: '/aco-assets/plat6-toll.webp', category: 'internal',
    match: /工程|改扩建|养护|招投标|项目管理|技术/i,
    planned: [
      { id: 'p-gc-1', name: '养护技术智能体' },
      { id: 'p-gc-2', name: '招投标管理智能体' },
    ],
  },
  {
    id: 'monitor', name: '运营管理部', aliases: ['营运管理部'], sub: '收费 · 路网调度 · 稽查',
    desc: '负责收费管理、路网调度、路况巡查与通行费稽查，可交办运营管理类文档、报表与专项分析。',
    duties: ['收费管理', '路网调度', '路况巡查', '通行费稽查'],
    accent: '#38bdf8', accentSoft: 'rgba(56,189,248,.35)',
    img: '/aco-assets/plat6-monitor.webp', category: 'internal',
    match: /收费|路网|路况|巡查|稽查|营运|调度|通行/i,
    planned: [
      { id: 'p-yy-1', name: '路网调度智能体' },
      { id: 'p-yy-2', name: '通行费稽查智能体' },
    ],
  },
  {
    // ⚠️ 沿用 'audit' id（复用其平台图，避免动 PERSONAS/映射）
    id: 'audit', name: '综合管理部', aliases: ['工程管理部'], sub: '服务区 · 智慧高速 · 运维',
    desc: '负责服务区运营、智慧高速、系统运维与数据管理，可交办相关管理类文档、方案与专项分析。',
    duties: ['服务区运营', '智慧高速', '系统运维', '数据管理'],
    accent: '#f43f5e', accentSoft: 'rgba(244,63,94,.35)',
    img: '/aco-assets/plat6-audit.webp', category: 'internal',
    match: /服务区|智慧高速|系统运维|运维|数据管理|机电/i,
    planned: [
      { id: 'p-gg-1', name: '智慧高速智能体' },
      { id: 'p-gg-2', name: '数据管理智能体' },
    ],
  },
  {
    id: 'tech', name: '技术研发公司', sub: '软件研发 · 数字人团队',
    desc: '外部技术合作研发团队。既可交办技术文档，也可将需求直接转入数字人研发办公室进行软件开发。',
    accent: '#34d399', accentSoft: 'rgba(52,211,153,.35)',
    img: '/aco-assets/plat6-tech.webp', category: 'external',
    match: /开发|研发|代码|dev|接口/i,
    planned: [
      { id: 'devteam', name: 'AI协同开发', special: 'devteam' },
      { id: 'p-tech-2', name: '全栈开发智能体' },
      { id: 'p-tech-3', name: '接口联调智能体' },
    ],
  },
  {
    id: 'design', name: '设计公司', sub: '视觉设计 · 方案呈现',
    desc: '外部设计协作单位，负责视觉设计、品牌与界面方案，可交办设计说明、方案汇报等工作。',
    accent: '#a78bfa', accentSoft: 'rgba(167,139,250,.35)',
    img: '/aco-assets/plat6-design.webp', category: 'external',
    match: /设计|视觉|ui|品牌/i,
    planned: [
      { id: 'p-des-1', name: 'UI 设计智能体' },
      { id: 'p-des-2', name: '品牌视觉智能体' },
    ],
  },
];

/**
 * 已接入业务智能体的明确部门归属。不能只靠描述关键词猜测，否则“运维”会落到综合管理部，
 * “流量分析”又会因无关键词命中而走默认部门。返回 null 时才允许使用部门通用关键词匹配。
 */
export function businessAgentDeptId(identity: string): string | null {
  if (/agent_2f014fd1-811c-4075-94c1-fdf59932c4a7|流量分析智能体/i.test(identity)) return 'monitor';
  if (/agent_1d4adb6d-407e-498f-a6c6-7e481607bd8f|运维管理智能体/i.test(identity)) return 'toll';
  return null;
}

/* ============================================================
   v8 具象化办公 — 呈现方式与人物档案
   ============================================================ */

/** 呈现方式（页面右上角可切换，localStorage 记忆） */
export type OfficeModeKey =
  | 'floor' | 'three' | 'studio' | 'meeting' | 'summon' | 'corridor' | 'board' | 'map'
  | 'wall' | 'tower' | 'paper' | 'call' | 'org' | 'roster' | 'hall';

export const OFFICE_MODES: Array<{ key: OfficeModeKey; label: string; hint: string; glyph: string }> = [
  { key: 'floor', label: '公司全景', hint: '整层办公区尽收眼底，喊谁谁走过来', glyph: '🏢' },
  { key: 'three', label: '三维指挥室', hint: '真3D场景 · 镜头飞行 · 人物走位', glyph: '🎥' },
  { key: 'studio', label: '真人视频厅', hint: '视频数字人 · 素材放入即生效', glyph: '🎬' },
  { key: 'meeting', label: '全体会议', hint: '会议室主位，点名起立领任务', glyph: '🪑' },
  { key: 'summon', label: '单独召见', hint: '领导办公室，经理进门听令', glyph: '🚪' },
  { key: 'corridor', label: '走廊巡视', hint: '办公楼走廊，推谁的门谁接待', glyph: '🚶' },
  { key: 'board', label: '调度指挥屏', hint: '满屏调度看板+路网图+工作动态', glyph: '🗺️' },
  { key: 'map', label: '楼层活点图', hint: '平面图上人员真实走动', glyph: '📍' },
  { key: 'wall', label: '值班电视墙', hint: '分屏盯各部门实时忙碌', glyph: '📺' },
  { key: 'tower', label: '数字大楼', hint: '办公楼立面，哪层干活哪层亮灯', glyph: '🏬' },
  { key: 'paper', label: '公文流转', hint: '交办单盖章下发，呈批件回桌', glyph: '📮' },
  { key: 'call', label: '电话调度', hint: '桌面调度台，一键呼叫部门', glyph: '☎️' },
  { key: 'org', label: '组织指挥图', hint: '指令沿组织架构树下达回传', glyph: '🌳' },
  { key: 'roster', label: '干部点将台', hint: '工牌墙点将，谁被点谁出列', glyph: '🪪' },
  { key: 'hall', label: '沙盘展厅', hint: '第一版构图 · 六台同框 · 令牌调度', glyph: '🛰️' },
];

/* ---------- v8.1 一体化等距楼层（公司全景模式） ----------
 * 整层楼是一张等距轴测渲染图，各部门是同一层里的分区。
 * 锚点为图内百分比坐标（已按生成图目检校准）：
 * x,y=经理站位  sx,sy=执行人站位  scale=近大远小 */
export interface FloorZone { x: number; y: number; sx: number; sy: number; scale: number }

export const FLOOR_SCENES: Record<DeptCategory, {
  bg: string;
  /** 中央指挥/前台锚点（派单光点起点） */
  hub: { x: number; y: number };
  /** 领导面前的站位（传唤/呈报时经理走到这里，近大远小放大） */
  front: { x: number; y: number };
  zones: Record<string, FloorZone>;
}> = {
  internal: {
    // 一整层：左上=收费运营分析区(车流大屏) 右上=运维监测区(监控电视墙)
    // 左下=稽查办案区(会议桌档案柜) 右下=收费站综合调度区(弧形座席)，中央=圆形前台
    bg: '/aco-assets/floor-internal.webp',
    hub: { x: 49, y: 50 },
    front: { x: 50, y: 96 },
    zones: {
      toll:    { x: 30, y: 42, sx: 22, sy: 33, scale: 0.82 },
      monitor: { x: 70, y: 42, sx: 79, sy: 34, scale: 0.82 },
      audit:   { x: 30, y: 66, sx: 20, sy: 73, scale: 1 },
      station: { x: 69, y: 64, sx: 79, sy: 70, scale: 1 },
    },
  },
  external: {
    // 一整层：左=软件研发公司工位区，右=设计公司工作室，中间=公共洽谈区
    bg: '/aco-assets/floor-external.webp',
    hub: { x: 48, y: 58 },
    front: { x: 50, y: 96 },
    zones: {
      tech:   { x: 31, y: 62, sx: 20, sy: 50, scale: 0.95 },
      design: { x: 67, y: 56, sx: 77, sy: 63, scale: 0.95 },
    },
  },
};

/* ---------- v12 用户素材版沙盘展厅 ----------
 * 用户用 GPT 生成的玻璃树脂工作台（数字员工坐姿办公+部门色灯环）+
 * 斜俯视深蓝地面背景。双页各 5 个弧形槽位：不足 5 个部门的槽位用
 * 灰色"待接入"工作台占位（pending=真部门素材待补仍可交办，expand=纯扩展位）。
 * 右侧槽位用左侧图的水平镜像保证弧形对称。 */
export const HALL_USER_BG = '/aco-assets/hall-floor-a.webp';

/** 可切换的展厅地面：斜俯透视（与工作台同视角，台子贴地）+ 简约流光路网交通地图勾勒，深蓝系与工作台协调。
 * ⚠️ 教训：背景必须是"站着微微低头向前看"的斜俯透视地面（近大远小、线条向上方灭点汇聚、远端渐暗），
 * 正俯视平面图会让工作台像漂浮的贴纸。 */
/** v26 飞行展厅：默认=无人机前飞航拍视频背景（工作台化身悬浮飞行台），静态地面保留可切换。
 * video 字段存在即为视频背景（img 作为切换器缩略图与视频加载前的垫图）。 */
export const HALL_FLOORS: Array<{ key: string; label: string; img: string; video?: string }> = [
  /* fly-bg2=1080p 首尾帧相同的真无缝循环版（首尾帧差 mean1.8，seedance first_frame+last_frame 同图生成） */
  { key: 'fly', label: '航拍飞行', img: '/aco-assets/hwr-09.webp', video: '/aco-assets/fly-bg2.mp4' },
  /* fly-bg3=用户即梦重做，4K原片压1080p(8.2MB)，夜航拍路网悬停俯瞰，靠交叉溶解转场接循环 */
  { key: 'fly2', label: '航拍飞行 II', img: '/aco-assets/fly-bg3-poster.webp', video: '/aco-assets/fly-bg3.mp4' },
  { key: 'floor-01', label: '深蓝拉丝', img: '/aco-assets/floor-01.webp' },
  { key: 'hwr-02', label: '高速路网', img: '/aco-assets/hwr-02.webp' },
  { key: 'hwp-07', label: '车流光轨', img: '/aco-assets/hwp-07.webp' },
  { key: 'hwp-08', label: '枢纽苜蓿叶', img: '/aco-assets/hwp-08.webp' },
  { key: 'hwr-09', label: '路网星图', img: '/aco-assets/hwr-09.webp' },
];

/** v20：整体放大一档、两侧槽位向中间收（填满圆环内空间，别放大过头） */
export const HALL_SLOTS: Array<{ x: number; y: number; scale: number }> = [
  { x: 16, y: 59, scale: 1.2 },
  { x: 33, y: 41, scale: 0.98 },
  { x: 50, y: 29, scale: 0.87 },
  { x: 67, y: 41, scale: 0.98 },
  { x: 84, y: 59, scale: 1.2 },
];

export type HallEntry =
  | { kind: 'dept'; deptId: string; img: string; mirror?: boolean }
  | { kind: 'pending'; deptId: string; img: string; mirror?: boolean }
  | { kind: 'expand'; img: string; mirror?: boolean; label?: string };

export const HALL_PAGES: Record<DeptCategory, HallEntry[]> = {
  internal: [
    { kind: 'dept', deptId: 'toll', img: '/aco-assets/platg-toll.webp' },        // 工程技术部
    { kind: 'dept', deptId: 'monitor', img: '/aco-assets/platg-monitor.webp' },   // 运营管理部
    { kind: 'dept', deptId: 'audit', img: '/aco-assets/platg-audit2.webp' },      // 综合管理部(居中·正面台)
    { kind: 'expand', img: '/aco-assets/platg-gray2.webp', mirror: true },        // 待接入(灰)
    { kind: 'expand', img: '/aco-assets/platg-gray2.webp', mirror: true },        // 待接入(灰)
  ],
  external: [
    { kind: 'dept', deptId: 'tech', img: '/aco-assets/platg-tech.webp' },
    { kind: 'dept', deptId: 'design', img: '/aco-assets/platg-design.webp' },
    /* 中间槽位=正面朝下视角（platg-grayf 由 image-edit 转正面而来），两侧仍用斜角灰台 */
    { kind: 'expand', img: '/aco-assets/platg-grayf.webp' },
    { kind: 'expand', img: '/aco-assets/platg-gray2.webp', mirror: true },
    { kind: 'expand', img: '/aco-assets/platg-gray2.webp', mirror: true },
  ],
};

/* ---------- v1 整图场景（沙盘展厅=用户拍板的第一版构图） ----------
 * 背景与六个部门工作台是一整张图（点阵地面+霓虹圆台一体渲染），
 * 交互层（热点/名牌/状态光效/数字员工/气泡）按锚点叠加其上。
 * spot: x,y=台面中心  by=底座落地线  rx=光效椭圆半宽(%视口)  scale=近大远小 */
export const HALL_V1_BG = '/aco-assets/hall-v1.webp';

export interface V1Spot { x: number; y: number; by: number; rx: number; scale: number }

export const V1_SPOTS: Record<string, V1Spot> = {
  toll:    { x: 12.5, y: 57, by: 68, rx: 10,  scale: 1.15 },
  monitor: { x: 30,   y: 40, by: 50, rx: 8.2, scale: 0.95 },
  audit:   { x: 47.5, y: 31, by: 40, rx: 7.6, scale: 0.88 },
  tech:    { x: 63,   y: 36, by: 45, rx: 7.6, scale: 0.9 },
  design:  { x: 78,   y: 44, by: 54, rx: 8.2, scale: 1 },
  station: { x: 90,   y: 56, by: 66, rx: 9,   scale: 1.1 },
};

/* ---------- v10 状态化沙盘（沙盘展厅模式） ----------
 * 部门=圆底工作台沙盘（业务场景做背幕+办公小人），三态图由图生图编辑保证同一主体：
 * idle=静置  active=点击选中(聚光+光环)  work=办理中(屏幕亮起/人物忙碌/灯带)
 * 站台旁的数字员工=机器人形象，三姿势 idle/greet(接令致意)/present(捧板呈报) */
export const HALL_V10_BG = '/aco-assets/hall-v10.webp';

export type DioState = 'idle' | 'active' | 'work';
export type BotState = 'idle' | 'greet' | 'present';

export const dioImg = (deptId: string, state: DioState) => `/aco-assets/dio2-${deptId}-${state}.webp`;
export const botImg = (deptId: string, state: BotState) => `/aco-assets/bot-${deptId}-${state}.webp`;

/** 每部门的人物档案（部门经理对接用户，助理与员工承担执行的"演出"） */
export interface DeptPersona {
  manager: string;      // 部门经理称呼
  assistant: string;    // 默认执行的部门助理称呼
  managerImg: string;   // 经理全身立绘(透明底，写实)
}

/** 领导定案：对用户一律用"AI助理"概念，不再用"部门经理"人设 */
export const PERSONAS: Record<string, DeptPersona> = {
  toll:    { manager: 'AI助理', assistant: '助理小费', managerImg: '/aco-assets/mgr-toll-v2.webp' },
  monitor: { manager: 'AI助理', assistant: '助理小监', managerImg: '/aco-assets/mgr-monitor-v2.webp' },
  audit:   { manager: 'AI助理', assistant: '助理小稽', managerImg: '/aco-assets/mgr-audit-v2.webp' },
  station: { manager: 'AI助理', assistant: '助理小站', managerImg: '/aco-assets/mgr-station-v2.webp' },
  tech:    { manager: 'AI助理', assistant: '助理小码', managerImg: '/aco-assets/mgr-tech-v2.webp' },
  design:  { manager: 'AI助理', assistant: '助理小美', managerImg: '/aco-assets/mgr-design-v2.webp' },
};

/** 部门办公室内景（写实效果图，格子楼/大楼剖面共用） */
export const ROOM_IMGS: Record<string, string> = {
  toll: '/aco-assets/room-toll.webp',
  monitor: '/aco-assets/room-monitor.webp',
  audit: '/aco-assets/room-audit.webp',
  station: '/aco-assets/room-station.webp',
  tech: '/aco-assets/room-tech.webp',
  design: '/aco-assets/room-design.webp',
};

/** 通用员工立绘（透明底，各办公室复用做"在岗员工"） */
export const STAFF_IMGS = ['/aco-assets/staff-a-v2.webp', '/aco-assets/staff-b-v2.webp', '/aco-assets/staff-c-v2.webp'];

/** 领导办公室第一人称背景（召见模式） */
export const LEADER_OFFICE_BG = '/aco-assets/leader-office-v3.webp';

/** 指挥台能力栏：口头汇报/文档/PPT/表格/设计图（文档二级选择 Word 或 PDF） */
export const CAPABILITIES = [
  { key: 'text', label: '口头汇报', hint: '直接答复', glyph: '💬' },
  { key: 'doc', label: '文档编写', hint: 'Word / PDF', glyph: '📄' },
  { key: 'ppt', label: 'PPT 编写', hint: '大纲·设计·成片', glyph: '📽️' },
  { key: 'excel', label: '表格绘制', hint: '.xlsx', glyph: '📊' },
  { key: 'design', label: '设计图生成', hint: 'AI 出图', glyph: '🎨' },
] as const;

export type CapabilityKey = typeof CAPABILITIES[number]['key'];

/** 智能工坊产物类型（能力栏映射到后台 resultType） */
export type DocTypeKey = 'text' | 'word' | 'pdf' | 'excel';

/** 系统设置「分环节应用模型」中 AI 指挥中心的任务类型。 */
export const AI_COMMAND_OFFICE_TASK_TYPE = 'ai-command-office';

/** 智能工坊默认参数（领导视角一律默认最优，不暴露技术选项） */
export const WORKSHOP_DEFAULTS = {
  kbDepth: 20,          // 知识库强匹配
  useKb: true,          // 默认引用知识库
  enableThinking: true, // 默认开启思考
  isPrivate: true,
};
